const SUPPORT = true;

const FOUR_LEVELS = {
  type: "levels",
  eyebrow: "Our Approach",
  heading: "We believe in working on four levels",
  intro:
    "WOFF uses a multi-pronged approach to give children with disabilities a head start in life. We believe in demedicalising the process and doing evidence-driven work with international standards, with people and community at the heart of the work.",
  items: [
    { title: "Individual or group level", text: "We work with children one-on-one or in group settings to support their development." },
    { title: "Family level", text: "We provide families with essential resources they need, including training them and connecting them with government programs." },
    { title: "Community level", text: "We work with local community infrastructure, such as schools, hospitals, and more, to ensure the child has the support they need." },
    { title: "Policy level", text: "We advocate for policy change to ensure inclusivity." },
  ],
};

export const PAGES = {
  "about-us": {
    eyebrow: "About Us",
    title: "Empowering Children with Disabilities in Telangana",
    image: "/images/p11.jpg",
    intro:
      "Wings of Fire Foundation was registered as a Section 8 Company under the Companies Act, 2013. We started our work in 2026. The foundation was established with a vision to serve humanity through compassion, dedication, and sustainable social development. It is a not-for-profit organization with fully charitable objectives.",
    blocks: [
      { type: "banner", image: "/images/g1.jpg", caption: "Inspired by Dr. A. P. J. Abdul Kalam & Ratan Tata" },
      {
        type: "text",
        eyebrow: "Our Mission",
        heading: "Creating opportunities for a brighter future",
        paras: [
          "Inspired by the vision, compassion, and leadership exemplified by Dr. A. P. J. Abdul Kalam and Ratan Tata, Wings of Fire Foundation is dedicated to creating opportunities for education, healthcare, skill development, and sustainable community welfare. Every contribution helps build a brighter future for those who need it most.",
          "Wings of Fire Foundation aims to touch the lives of lakhs of people affected by poverty, illness, and hardship. The foundation is committed to providing lasting healthcare solutions, supporting quality education, promoting skill development, generating employment opportunities, and nurturing leadership among deserving students from underprivileged communities. We also strive to create sustainable models of social and economic development in villages and urban communities, empowering individuals and building stronger, self-reliant societies.",
        ],
        image: "/images/g2.jpg",
      },
      {
        type: "quote",
        heading: "Ignite Awareness, Inspire Change.",
        paras: [
          "WOFF is a dedicated organization committed to empowering individuals with disabilities, particularly in marginalized communities. Our mission is to advocate for their rights and facilitate access to education, healthcare, and employment opportunities.",
          "We work in Telangana, as well as with partner organizations across India to ensure access and equity.",
        ],
      },
      {
        type: "stats",
        eyebrow: "By the numbers",
        heading: "WOFF is deeply entrenched in the communities and ecosystems we work in.",
        items: [
          { value: "1.5K+", title: "Children, Students & Persons with Disabilities", text: "And their families assisted throughout Wings of Fire Foundation" },
          { value: "3+", title: "Years Of Experience", text: "Years of history serving our communities" },
          { value: "1K+", title: "Calls", text: "On the National Disability Helpline" },
          { value: "50+", title: "Workshops, trainings, conferences", text: "Across India on inclusivity for people with disabilities" },
          { value: "13+", title: "Research Studies", text: "Published on education access, early childhood care and development, and more for children with disabilities" },
          { value: "500", title: "Children with disabilities every year", text: "Impacted by WOFF work in ECCD and Inclusive Education" },
        ],
      },
      {
        type: "text",
        eyebrow: "Our Work",
        heading: "Holistic Community-Based Development",
        paras: [
          "Our work aims to dismantle barriers and foster an inclusive society where every person with a disability can achieve self-sufficiency and actively participate in their community.",
        ],
        image: "/images/p13.jpg",
        reverse: true,
      },
    ],
  },

  "areas-of-work": {
    eyebrow: "Areas of Work",
    title: "Our Activities",
    image: "/images/p7.jpg",
    intro: "Scholarship programs and awareness programs held by Wings of Fire Foundation for students and communities across Telangana.",
    blocks: [
      {
        type: "features",
        items: [
          {
            tag: "Scholarship Program",
            title: "Intermediate Students",
            text: "The students scholarship program held in Kaloji Kala Kshetram is aimed at students who have achieved intermediate marks of above 900, providing opportunities for 1200 deserving students.",
            image: "/images/g7.jpg",
          },
          {
            tag: "Scholarship Program",
            title: "SSC Students",
            text: "The students scholarship program held in Kaloji Kala Kshetram is aimed at students who have achieved SSC marks of 550, providing opportunities for 1000 deserving students.",
            image: "/images/g5.jpg",
          },
          {
            tag: "Awareness Program",
            title: "Anti-Suicide Seminar",
            text: "An important Anti-suicide seminar was held by our organization to address crucial mental health issues and provide support and resources to those in need. Our dedicated team engaged the community with valuable insights and strategies aimed at preventing suicide and promoting emotional well-being.",
            image: "/images/g4.jpg",
          },
        ],
      },
      { type: "programs" },
    ],
  },

  "early-childhood-care-and-development-eccd": {
    eyebrow: "Our Programs",
    title: "Early Childhood Care & Development (ECCD)",
    image: "/images/g2.jpg",
    intro: "Chahak: A much needed head start for children with disabilities.",
    blocks: [
      FOUR_LEVELS,
      {
        type: "text",
        eyebrow: "Our Process",
        heading: "A 3-month plan, built with the family",
        paras: [
          "First, an assessment of the child, in close consultation of the family, is done to understand the needs and abilities of the child. Then, WOFF provides a 3-month plan and goals for the child, set with the family, community, and child’s surroundings in mind.",
          "In this plan, WOFF provides group and individual sessions to children, filled with exercises to engage and teach children. In addition, WOFF provides training for families, school teachers and Anganwadi workers, and community members on how best to treat the child. WOFF also provides nutritional counseling for a holistic approach for the child’s wellbeing and works with the family on gaining their government-ordained rights and entitlements.",
        ],
        image: "/images/p8.jpg",
      },
      {
        type: "cards",
        eyebrow: "Our Services",
        heading: "What we do with every child",
        items: [
          { title: "Group and Individual Sessions", text: "Group sessions are important for teaching children how to interact with other children and individualised child-centric planning is also done to ensure children are getting their unique needs met. Sessions are now being conducted online as well as in-person, with children being provided regular teaching learning material (TLM) kits." },
          { title: "Therapy Services", text: "Our team of experienced therapists provides individualized treatment plans to address specific needs and help children reach their full potential." },
          { title: "Play-Based Learning", text: "Through interactive and engaging activities, children develop essential cognitive, social, and emotional skills while having fun." },
          { title: "Sensory and Mobility Exercises", text: "WOFF works with children on hearing, smelling, touching, tasting, and introducing diverse voices/sounds through parents’ ADLs. These exercises engage children and have been extremely successful in some cases, when children have gone from lying down and being in isolation all day to independent walking." },
          { title: "Communication", text: "WOFF conducts communication exercises with children with their unique needs in mind. Children with exposure to the outside world have vastly improved their vocabulary and can now speak basic words, such as Ma, Mami, and Doodh (milk), and can even form basic sentences." },
          { title: "Addressing Malnutrition", text: "WOFF helps with Anganwadi enrollment, nutrition support, mothers’ knowledge-building, independent eating exercises, and much more. We also grow nutrition terrace gardens in urban slums and provide a direct supply of milk, eggs, and nutritionally-dense foods for vulnerable families. We help families get ration cards to avail government resources." },
        ],
      },
      {
        type: "quote",
        eyebrow: "Our Impact",
        heading: "Helping Children with Disabilities from the Very Beginning",
        paras: [
          "Through WOF Foundation focused efforts on ECCD, we’ve seen improved developmental outcomes, increased family and community awareness on the importance of early intervention, and a brighter future for some of our community’s most vulnerable children.",
        ],
      },
      { type: "stories", program: "ECCD" },
    ],
    support: SUPPORT,
  },

  "inclusive-education": {
    eyebrow: "Our Programs",
    title: "Inclusive Education",
    image: "/images/p9.jpg",
    intro:
      "Children with disabilities are the highest out of school group in the country. WOFF ensures the right to education starts with identifying children with disabilities of school-going age, creating a demand for education within the family of the child, supporting in the rehabilitation and learning capabilities of the child, liaisoning and training with the school system to include the child and enable education and allied rights.",
    blocks: [
      FOUR_LEVELS,
      {
        type: "cards",
        eyebrow: "Ensuring Access",
        heading: "Enabling a child's right to education",
        items: [
          { title: "Accessible Learning Materials", text: "We develop and provide learning materials that are accessible to children with different disabilities, ensuring they can fully engage in the learning process." },
          { title: "Assessment & Intervention", text: "We conduct screenings to identify children with disabilities as early as possible, ensuring children don't drop out of school due to a lack of support." },
          { title: "Teacher Training", text: "We provide comprehensive training to teachers to equip them with the knowledge and skills needed to support children with disabilities in the classroom." },
          { title: "Individualised Learning Plans", text: "We create personalized learning plans for each child, taking into account their unique strengths, challenges, and learning styles." },
        ],
      },
      {
        type: "text",
        eyebrow: "Our Impact",
        heading: "Equal Access to Learning Opportunities",
        paras: [
          "By collaborating with schools, educators, and communities, we work to create environments where all learners can thrive, highlighting the importance of diversity and inclusion in education. Our impact on inclusive education not only benefits students with disabilities but also enriches the learning experience for all, preparing a generation that values equity, respect, and empathy.",
          "Our research on inclusive education dives deep into understanding the barriers and opportunities within the educational system for students with disabilities. By analyzing policies, practices, and personal experiences, we are at the forefront of advocating for changes that make education truly inclusive.",
        ],
        image: "/images/g3.jpg",
        reverse: true,
        cta: { label: "View All Research", href: "/information-hub" },
      },
      { type: "stories", program: "Inclusive Education" },
    ],
    support: SUPPORT,
  },

  "life-skills-for-young-adults-with-disabilities": {
    eyebrow: "Our Programs",
    title: "Life Skills For Young Adults with Disabilities",
    image: "/images/p8.jpg",
    intro: "Unlocking Potential Together: Ensuring Community-Based Independent Living through Essential Life Skills Training.",
    blocks: [
      {
        type: "text",
        eyebrow: "Our Life Skills Program",
        heading: "Towards a dignified, independent life",
        paras: [
          "WOFF runs a life skills program for young adults with disabilities with an aim to make individuals independent towards a dignified living. Nature classrooms, sexuality education, heritage exposures, music and sports form critical areas of the curriculum.",
          "Our life skills program recognizes the importance of equipping young adults with disabilities with the necessary tools and knowledge to lead independent and fulfilling lives. Through a comprehensive curriculum and personalized support, we empower them to overcome challenges and achieve their full potential.",
        ],
        checks: [
          "Providing personalized guidance for developing essential life skills",
          "Building confidence and fostering self-reliance in daily activities",
          "Promoting social inclusion and community engagement for young adults",
        ],
        image: "/images/g4.jpg",
      },
      {
        type: "cards",
        eyebrow: "Our Services",
        heading: "How we support young adults",
        items: [
          { title: "Mentorship Program", text: "Through our mentorship program, we provide guidance and support to young adults with disabilities. We provide ongoing support and monitor the progress of each young adult in the program to ensure their success and wellbeing." },
          { title: "Assessment & Goal-Setting", text: "Our program begins with comprehensive assessments to identify individual strengths and areas for growth. Based on the assessment results, personalized goals are set to guide the skill development process." },
          { title: "Skill Development Workshops", text: "We conduct life skills workshops to empower young adults with disabilities and enhance their independence." },
        ],
      },
      {
        type: "quote",
        eyebrow: "Our Impact",
        heading: "Equipping Young Adults with confidence & independence",
        paras: [
          "Through tailored workshops, mentorship, and practical experiences, we ensure that youth with disabilities develop the resilience and competencies needed to achieve their personal and professional goals. Our impact extends beyond individual growth, as we foster a generation of empowered youth who are ready to contribute to their communities and advocate for their rights.",
        ],
      },
    ],
    support: SUPPORT,
  },

  "research-and-policy-advocacy": {
    eyebrow: "Our Programs",
    title: "Research & Policy Advocacy",
    image: "/images/p10.jpg",
    intro:
      "Our research focuses on the impact of early intervention, inclusive education, and community-based approaches in improving the lives of children with disabilities. Through rigorous studies and analysis, we strive to identify effective strategies and advocate for their implementation.",
    blocks: [
      {
        type: "cards",
        eyebrow: "Our Research",
        heading: "Advancing Research for Inclusive Education & Early Intervention",
        items: [
          { title: "Methods & Tools", text: "We utilize a variety of research methods and tools, including surveys, interviews, and case studies, to gather valuable insights." },
          { title: "Evidence-Led Decisions", text: "Our team conducts thorough data analysis to ensure that our decisions are well-informed and based on solid evidence." },
          { title: "Ongoing Research", text: "We are committed to ongoing research to improve and develop impactful programs that address the needs of children with disabilities." },
        ],
      },
      { type: "reports" },
    ],
    support: SUPPORT,
  },

  "network-trainings-and-partnership-building": {
    eyebrow: "Our Programs",
    title: "Network, Trainings, and Partnership Building",
    image: "/images/p13.jpg",
    intro: "Extending our reach beyond our traditional audiences.",
    blocks: [
      {
        type: "text",
        eyebrow: "About Our Partners",
        heading: "Inclusion matters everywhere",
        paras: [
          "Although we are a disability organization, our reach and scope go far beyond, engaging in state and national alliances across various sectors including the Right to Education Forum and Right to Food initiatives. Our involvement in these traditionally non-disability spaces underlines our conviction that voices and needs of people with disabilities deserve recognition and representation everywhere.",
          "We are committed to partnership-building with a diverse range of organizations, including those working within railway stations, shelter homes, and with unhoused populations. Our focus is not limited to disability organizations; we aim to infuse the disability narrative into various contexts.",
        ],
        image: "/images/p2.jpg",
      },
      {
        type: "text",
        eyebrow: "About The Program",
        heading: "Building networks, fostering partnerships",
        paras: [
          "WOFF Network, Trainings, and Partnership Building program is designed to extend the impact of our work beyond traditional disability spaces, reflecting our belief that inclusion matters everywhere. This program is key to deepening our understanding of broader research work and societal needs, allowing us to tailor our advocacy and interventions more effectively.",
          "Our training initiatives are comprehensive, targeting government functionaries, non-governmental organizations, and other key stakeholders. These training sessions are designed to equip a wide array of individuals and groups with the knowledge and skills necessary to better serve and include people with disabilities in their communities.",
        ],
        image: "/images/p3.jpg",
        reverse: true,
      },
      {
        type: "quote",
        eyebrow: "Partner With Us",
        heading: "Providing trainings & capacity-building workshops",
        paras: [
          "Reach out to us to arrange trainings and capacity-building workshops for your organization. We work with nonprofits, schools, governmental functionaries, and more to raise awareness about disability support.",
        ],
        cta: { label: "Contact Us", href: "/contact-and-connect" },
      },
    ],
    support: SUPPORT,
  },

  "information-hub": {
    eyebrow: "Information Hub",
    title: "Reports, research & resources",
    image: "/images/p4.jpg",
    intro: "Our publications on education access, early childhood care and development, and more for children with disabilities.",
    blocks: [{ type: "reports" }],
    support: SUPPORT,
  },

  "research-studies": {
    eyebrow: "Research Studies",
    title: "Research Studies",
    image: "/images/p9.jpg",
    intro: "[Research studies content to be added by Wings of Fire Foundation]",
    blocks: [{ type: "reports" }],
    support: SUPPORT,
  },

  "diversity-and-equal-oppertunity-policy": {
    eyebrow: "Policy",
    title: "Diversity and Equal Opportunity Policy",
    image: "/images/g3.jpg",
    intro: "[Diversity and equal opportunity policy text to be added by Wings of Fire Foundation]",
    blocks: [],
    support: SUPPORT,
  },
};

export const REPORTS = [
  { title: "Annual Report 2022-2023", tag: "Annual Report" },
  { title: "National Consultation on the Impact of Covid", tag: "Consultation" },
  { title: "Navigating Health and Education", tag: "Study" },
];

export const GALLERY = ["p13", "g7", "p2", "g4", "p6", "g3", "p10", "g5", "p8", "g6", "p3", "p12", "p9", "p11", "p7", "g2"];
